import { useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";
import { formatLocalDateTime } from "../../utils/dateTime";

function todayYmd() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function MessageReport() {
  const [from, setFrom] = useState(todayYmd());
  const [to, setTo] = useState(todayYmd());
  const [source, setSource] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [limit, setLimit] = useState(500);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (from) params.from = from;
      if (to) params.to = to;
      if (source && source !== "ALL") params.source = source;
      if (status && status !== "ALL") params.status = status;

      const res = await axios.get("/whatsapp/report/messages", { params });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to load report");
      setRows([]);
    }
    setLoading(false);
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = { format: "csv", limit };
      if (from) params.from = from;
      if (to) params.to = to;
      if (source && source !== "ALL") params.source = source;
      if (status && status !== "ALL") params.status = status;

      const res = await axios.get("/whatsapp/report/messages", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "messages_report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to download CSV");
    }
    setDownloading(false);
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-50">
      <SidebarClient />
      <div className="flex-1 p-3 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-6 gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-extrabold text-gray-900">Message Report</h1>
            <p className="text-gray-500 mt-1 text-xs md:text-sm">Date-wise report with MANUAL vs API source.</p>
          </div>
          <button
            onClick={downloadCsv}
            disabled={downloading}
            className="bg-gray-900 text-white px-4 md:px-6 py-2 rounded-xl font-bold hover:bg-gray-800 transition disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
          >
            {downloading ? "Downloading..." : "Download CSV"}
          </button>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">FROM</label>
              <div
                className="w-full border p-3 rounded-xl mt-1 cursor-pointer focus-within:ring-2 focus-within:ring-blue-500"
                onClick={() => {
                  fromRef.current?.showPicker?.();
                  fromRef.current?.focus();
                }}
              >
                <input
                  ref={fromRef}
                  type="date"
                  className="w-full bg-transparent outline-none"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">TO</label>
              <div
                className="w-full border p-3 rounded-xl mt-1 cursor-pointer focus-within:ring-2 focus-within:ring-blue-500"
                onClick={() => {
                  toRef.current?.showPicker?.();
                  toRef.current?.focus();
                }}
              >
                <input
                  ref={toRef}
                  type="date"
                  className="w-full bg-transparent outline-none"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">SOURCE</label>
              <select
                className="w-full border p-3 rounded-xl mt-1"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="ALL">All</option>
                <option value="MANUAL">Manual</option>
                <option value="API">API</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">STATUS</label>
              <select
                className="w-full border p-3 rounded-xl mt-1"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="ALL">All</option>
                <option value="PENDING">PENDING</option>
                <option value="SENT">SENT</option>
                <option value="DELIVERED">DELIVERED</option>
                <option value="READ">READ</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">LIMIT</label>
              <input
                type="number"
                className="w-full border p-3 rounded-xl mt-1"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value) || 500)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              onClick={fetchReport}
              disabled={loading}
              className="bg-blue-600 text-white px-4 md:px-6 py-2 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50 text-sm md:text-base flex-1 sm:flex-none"
            >
              {loading ? "Loading..." : "Search"}
            </button>
            <button
              onClick={() => {
                const t = todayYmd();
                setFrom(t);
                setTo(t);
              }}
              className="bg-gray-100 text-gray-700 px-4 md:px-6 py-2 rounded-xl font-bold hover:bg-gray-200 transition text-sm md:text-base flex-1 sm:flex-none"
            >
              Today
            </button>
          </div>
        </div>

        <div className="mt-4 md:mt-6 bg-white p-3 md:p-6 rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-gray-500 text-sm md:text-base">No records found.</p>
          ) : (
            <div className="overflow-x-auto -mx-3 md:mx-0">
              <table className="w-full text-xs md:text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b bg-gray-50">
                    <th className="py-2 md:py-3 px-2 md:px-4 whitespace-nowrap">Time</th>
                    <th className="py-2 md:py-3 px-2 md:px-4 whitespace-nowrap">To</th>
                    <th className="py-2 md:py-3 px-2 md:px-4 whitespace-nowrap">Type</th>
                    <th className="py-2 md:py-3 px-2 md:px-4 whitespace-nowrap">Source</th>
                    <th className="py-2 md:py-3 px-2 md:px-4 whitespace-nowrap">Status</th>
                    <th className="py-2 md:py-3 px-2 md:px-4 whitespace-nowrap">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="py-2 md:py-3 px-2 md:px-4 text-gray-700 whitespace-nowrap">
                        {formatLocalDateTime(m.created_at)}
                      </td>
                      <td className="py-2 md:py-3 px-2 md:px-4 font-mono text-gray-800 whitespace-nowrap text-xs md:text-sm">{m.to_number}</td>
                      <td className="py-2 md:py-3 px-2 md:px-4 text-gray-700 whitespace-nowrap">{m.message_type}</td>
                      <td className="py-2 md:py-3 px-2 md:px-4">
                        <span className="px-2 md:px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-bold text-xs whitespace-nowrap">
                          {m.source || "MANUAL"}
                        </span>
                      </td>
                      <td className="py-2 md:py-3 px-2 md:px-4">
                        <span className="px-2 md:px-3 py-1 rounded-full bg-gray-100 text-gray-800 font-bold text-xs whitespace-nowrap">
                          {m.status}
                        </span>
                      </td>
                      <td className="py-2 md:py-3 px-2 md:px-4 text-gray-700 max-w-md truncate" title={m.content || ""}>
                        {String(m.content || "").slice(0, 120)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
